import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableContainer from "../../../components/Tables/TableContainer";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useMediaQuery } from "@mui/material";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { useNavigate } from "react-router-dom";
import { startGetAdsUser } from "../../../actions/user";
import {
  getGeneralStatistics,
  getStatusAdsCampaign,
} from "../../../services/adsCampaigns";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "../../../components/Form/TextField";
import { useForm } from "react-hook-form";
import {
  ADS_CAMPAIGN_STATUS_ACTIVE,
  ADS_CAMPAIGN_STATUS_DRAFT,
  ADS_CAMPAIGN_STATUS_FINISHED,
  ALERTS_CAMPAIGNS_TEXT_TABLE,
  META,
  SORT_DIRECTION,
  SORTABLE_COLUMNS,
  STATUS_SORT_ORDER,
  DEFAULT_SORT_CONFIG,
  ADS_CAMPAIGN_STATUS_DELETED,
  BORDER_RADIUS_BUTTON,
  FONT_SIZE_BUTTON,
  YELLOW_ALERT_TYPES,
  ALERT_COLORS,
  FONT_WEIGHT_BUTTON,
} from "../../../utils/constants";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SelectUser from "../../../components/Form/SelectUser";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";
import {
  CAMPAIGNS_STATUS_DRAFT,
  CAMPAIGNS_TABLE_ARIA_LABEL,
  COLUMN_NAME_ACTIONS,
  COLUMN_NAME_CAMPAIGN,
  COLUMN_NAME_CLICKS,
  COLUMN_NAME_CREATED_BY,
  COLUMN_NAME_PLATFORM,
  COLUMN_NAME_RESULTS,
  COLUMN_NAME_STATUS,
  DATE_SHORT_2,
  EMPTY_CAMPAIGNS,
  COLUMN_NAME_CREATED_DATE,
  LOADING,
  CAMPAIGNS_STATUS_FINISHED,
  ALERT_ARCHIVE_CAMPAIGN_TITLE,
  ALERT_ARCHIVE_CAMPAIGN_DESCRIPTION,
  BUTTON_ARCHIVE_CAMPAIGN,
  COLUMN_NAME_CTR,
  COLUMN_NAME_IMPRESSIONS,
  COLUMN_NAME_CPA,
  COLUMN_NAME_CPM,
  COLUMN_NAME_CPC,
  COLUMN_NAME_SPEND,
  ROAS_METRICS,
  FREQUENCY,
  REPORTS,
  VALUE_NUMBER,
  FILTER_BY_USERS_PLACEHOLDER,
  ALL_USERS,
  LIST_TABLE_CAMPAIGNS_TITLE,
  CAMPAIGN,
  CAMPAIGNS,
  ALERT_ARCHIVE_CAMPAIGN_TITLE_ACTIVE,
  CAMPAIGNS_STATUS_DELETED,
  STATUS_ACTIVE,
  STATUS_DEACTIVATED,
  YOUR_CAMPAIGN_HAS_SOME_DISAPPROVALS,
  CAMPAIGNS_SEARCHBAR_PLACEHOLDER,
  CAMPAIGNS_NOT_FOUND,
} from "../../../i18n/keysTranslations";
import {
  getCurrencyAdAccountPlatform,
  getRoleCapability,
} from "../../../actions/getters";
import {
  startDeleteAdsCampaign,
  startSearchAdsCampaignsByName,
} from "../../../actions/adsCampaigns";
import {
  getFormattedCampaign,
  logoPlatform,
  openLink,
} from "../../../utils/string";
import {
  getUniqueID,
  numberCompactFormat,
  numberDecimal,
} from "../../../utils/numbers";
import { ConfirmAlert } from "../../../components/Alerts/Alerts";
import Switch from "../../../components/Form/Switch";
import ButtonDuplicateCampaign from "../../../components/Buttons/ButtonDuplicateCampaign";
import Loader from "../../../components/Loaders/Loader";
import _ from "lodash";
import { ChartColumnBig } from "lucide-react";
import CellTypography from "../../../components/Tables/CellTypography";
import TableCell from "../../BillingAndPayments/components/TableCell";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../../utils/colors";
import CampaignStatisticsOverview from "../../../components/Statistics/CampaignStatisticsOverview";
import {
  commonPropsTableCellBody,
  commonPropsTableCellHeader,
  SkeletonCampaignMetric,
} from "../../../components/Tables/campaignsTableShared";

const MIN_LENGTH_TO_SEARCH = 1;

// Utility functions for sorting
const getPlatformSortValue = (platforms) =>
  platforms?.join(",").toLowerCase() || "";

const getStatusSortValue = (status) => STATUS_SORT_ORDER[status] || 0;

const getStatisticsValue = (statistics, key) => {
  const stats = getGeneralStatistics(statistics || {});
  return Number(stats[key]) || 0;
};

const AdsCampaignsTable = ({
  data = {},
  loading,
  onChangeStatusCampaign = () => {},
  lastItemRef,
  loadedLastCampaign,
  columnVisibility,
  onToggleColumn,
  onSetColumnVisibility,
  statisticsLoading = [],
  control,
  sortConfig = DEFAULT_SORT_CONFIG,
  onTableSort = () => {},
  hasActiveDateRange = false,
  dateRange = null, // { startDate, endDate } o null para métricas totales
  syncingCampaignIDs = [],
  updatingStatusCampaignIDs = [],
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const statusColumnWidth = i18n.language?.startsWith("en") ? 100 : 120;
  const alertColumnWidth = 18;

  const [users, setUsers] = useState({});
  const [isDeletingCampaign, setIsDeletingCampaign] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState({});
  const [isSearching, setIsSearching] = useState(false);

  const { control: searchControl } = useForm({
    defaultValues: {
      searchCampaigns: "",
    },
  });

  const canEditCampaign = dispatch(getRoleCapability("canEditCampaign"));
  const canCreateCampaign = dispatch(getRoleCapability("canCreateCampaign"));

  const onRunRemoteSearch = useCallback(
    async (term) => {
      if (!term || term.length < MIN_LENGTH_TO_SEARCH) {
        setSearchResults({});
        setIsSearching(false);
        return;
      }
      setIsSearching(true);

      const response = await dispatch(
        startSearchAdsCampaignsByName({
          name: term,
          minLength: MIN_LENGTH_TO_SEARCH,
        })
      );
      if (response?.ok) {
        setSearchResults(response.data || {});
      } else {
        setSearchResults({});
      }
      setIsSearching(false);
    },
    [dispatch]
  );

  // Inline debounce implementation (no additional imports)
  const debouncedSearchRef = useRef(null);
  useEffect(() => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    if (searchTerm && searchTerm.length >= MIN_LENGTH_TO_SEARCH) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      setSearchResults({});
    }
    debouncedSearchRef.current = setTimeout(() => {
      onRunRemoteSearch(searchTerm);
    }, 350);
    return () => clearTimeout(debouncedSearchRef.current);
  }, [searchTerm, onRunRemoteSearch]);

  const SortableHeader = ({
    columnKey,
    children,
    sx,
    stickyPosition,
    stickyOffset,
    ...props
  }) => (
    <TableCell
      {...commonPropsTableCellHeader}
      sx={sx}
      sortDirection={
        sortConfig.key === columnKey ? sortConfig.direction : false
      }
      stickyPosition={stickyPosition}
      stickyOffset={stickyOffset}
      {...props}
    >
      <TableSortLabel
        active={sortConfig.key === columnKey}
        direction={
          sortConfig.key === columnKey
            ? sortConfig.direction
            : SORT_DIRECTION.ASC
        }
        onClick={() => onTableSort(columnKey)}
        sx={{
          ".MuiTableSortLabel-icon": {
            fontSize: 15,
          },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  // Optimized sort value getter
  const getSortValue = useCallback(
    (campaign, columnKey) => {
      const sortValueMap = {
        [SORTABLE_COLUMNS.CAMPAIGN]: () =>
          (
            campaign?.platformsData?.[campaign?.platforms?.[0]]?.name ||
            campaign.name ||
            ""
          ).toLowerCase(),

        [SORTABLE_COLUMNS.PLATFORM]: () =>
          getPlatformSortValue(campaign.platforms),

        [SORTABLE_COLUMNS.CREATED_BY]: () =>
          (users[campaign.createdBy]?.name || "").toLowerCase(),

        [SORTABLE_COLUMNS.CREATION_TIME]: () => campaign.creationTime || 0,

        [SORTABLE_COLUMNS.IMPRESSIONS]: () =>
          getStatisticsValue(campaign.statistics, "impressions"),

        [SORTABLE_COLUMNS.CLICKS]: () =>
          getStatisticsValue(campaign.statistics, "click"),

        [SORTABLE_COLUMNS.CTR]: () =>
          getStatisticsValue(campaign.statistics, "ctr"),

        [SORTABLE_COLUMNS.SPEND]: () =>
          getStatisticsValue(campaign.statistics, "spend"),

        [SORTABLE_COLUMNS.ROAS]: () =>
          getStatisticsValue(campaign.statistics, "roas"),

        [SORTABLE_COLUMNS.CPM]: () =>
          getStatisticsValue(campaign.statistics, "cpm"),

        [SORTABLE_COLUMNS.CPC]: () =>
          getStatisticsValue(campaign.statistics, "cpc"),

        [SORTABLE_COLUMNS.RESULTS]: () =>
          getStatisticsValue(campaign.statistics, "mainAction"),

        [SORTABLE_COLUMNS.CPA]: () =>
          getStatisticsValue(campaign.statistics, "costPerMainAction"),

        [SORTABLE_COLUMNS.FREQUENCY]: () =>
          getStatisticsValue(campaign.statistics, "frequency"),

        [SORTABLE_COLUMNS.STATUS]: () => {
          const status = getStatusAdsCampaign({
            states: campaign.states,
            target: "general",
          });
          return getStatusSortValue(status);
        },
      };

      return sortValueMap[columnKey]?.() || "";
    },
    [users]
  );

  // Optimized sorting logic with search filter
  const keys = useMemo(() => {
    const searchActive =
      searchTerm && searchTerm.length >= MIN_LENGTH_TO_SEARCH;
    const sourceData = searchActive ? searchResults : data || {};

    let filteredKeys = Object.keys(sourceData);

    // Additional local inclusion filter when search is active
    if (searchActive) {
      const termLower = String(searchTerm).toLowerCase().trim();
      filteredKeys = filteredKeys.filter((id) => {
        const campaign = sourceData[id];
        if (!campaign) return false;
        const searchableName =
          campaign?.platformsData?.[campaign?.platforms?.[0]]?.nameLowercase ||
          "";
        return searchableName.includes(termLower);
      });
    }

    // Apply sorting
    const sortedKeys = filteredKeys.sort((a, b) => {
      const aValue = getSortValue(sourceData[a], sortConfig.key);
      const bValue = getSortValue(sourceData[b], sortConfig.key);

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortConfig.direction === SORT_DIRECTION.ASC
        ? comparison
        : -comparison;
    });

    return sortedKeys;
  }, [data, sortConfig, getSortValue, searchTerm, searchResults]);

  const getUser = async (userId) => {
    if (!users[userId]) {
      const response = await dispatch(startGetAdsUser(userId));
      if (response) {
        setUsers((prev) => ({ ...prev, [userId]: response }));
      }
    }
  };

  useEffect(() => {
    keys.forEach((key) => {
      if (data[key]?.createdBy) {
        getUser(data[key].createdBy);
      }
    });
    // eslint-disable-next-line
  }, [data]);

  const onDeleteDraftCampaign = async (e, campaignID, campaignData, status) => {
    e?.stopPropagation();
    const isActive = status === ADS_CAMPAIGN_STATUS_ACTIVE;
    const result = await ConfirmAlert({
      title: isActive
        ? t(ALERT_ARCHIVE_CAMPAIGN_TITLE_ACTIVE)
        : t(ALERT_ARCHIVE_CAMPAIGN_TITLE),
      text: t(ALERT_ARCHIVE_CAMPAIGN_DESCRIPTION),
      confirmButtonText: t(BUTTON_ARCHIVE_CAMPAIGN),
    });
    if (!result.isConfirmed) return;
    setIsDeletingCampaign((prev) => ({ ...prev, [campaignID]: true }));

    if (isActive) {
      const response = await onChangeStatusCampaign(campaignID);
      if (!response) {
        setIsDeletingCampaign((prev) => ({ ...prev, [campaignID]: false }));

        return;
      }
    }

    await dispatch(
      startDeleteAdsCampaign(
        getFormattedCampaign({
          data: { ...campaignData, id: campaignID },
          format: "load",
        })
      )
    );
    setIsDeletingCampaign((prev) => ({ ...prev, [campaignID]: false }));
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: { xs: "100%", md: "calc(100vw - 252px)" },
        px: 3,
      }}
    >
      {/* Total cards */}
      <CampaignStatisticsOverview
        dateRange={dateRange}
        isLoading={hasActiveDateRange ? !_.isEmpty(statisticsLoading) : false}
      />

      {/* Primera fila: Título + Chip + Filtros */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">{t(LIST_TABLE_CAMPAIGNS_TITLE)}</Typography>
          <Chip
            label={`${keys.length} ${keys.length === 1 ? t(CAMPAIGN) : t(CAMPAIGNS)}`}
            size="small"
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#666",
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-end", sm: "flex-end" },
          }}
        >
          <SelectUser
            name="createdBy"
            control={control}
            sx={{
              borderRadius: BORDER_RADIUS_BUTTON,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              minWidth: "max-content",
              width: { xs: "auto", sm: "auto" },
              pl: 1.5,
              height: 48,
              mr: 1,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
            sxText={{
              fontSize: FONT_SIZE_BUTTON,
              fontWeight: FONT_WEIGHT_BUTTON,
            }}
            mode="text"
            onGetDisplayText={(field, options) => {
              if (_.isEmpty(field?.value)) {
                return t(FILTER_BY_USERS_PLACEHOLDER);
              }

              const option = options.find(
                (option) => option.value === field.value
              );

              return option?.label || "";
            }}
            showEmptyOption={true}
            emptyOptionText={t(ALL_USERS)}
            startIcon={
              <PersonOutlineIcon
                sx={{ fontSize: 20, color: "text.secondary" }}
              />
            }
          />

          <ColumnVisibilityMenu
            columnVisibility={columnVisibility}
            onToggleColumn={onToggleColumn}
            onSetColumnVisibility={onSetColumnVisibility}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <TextField
          name="searchCampaigns"
          control={searchControl}
          placeholder={t(CAMPAIGNS_SEARCHBAR_PLACEHOLDER)}
          variant="filled"
          fullWidth={false}
          rules={{}}
          actionsChange={(value) => setSearchTerm(value)}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_FIELDS,
            },
            "& .MuiInputBase-input": {
              padding: "6px 12px",
            },
          }}
          startIcon={
            <SearchIcon sx={{ color: "#999999 !important", fontSize: 20 }} />
          }
        />
      </Box>

      <TableContainer
        keys={keys}
        emptyText={EMPTY_CAMPAIGNS}
        ariaLabel={t(CAMPAIGNS_TABLE_ARIA_LABEL)}
        hasPagination={false}
        hasMarginBottom={false}
        showEmptyText={false}
        sx={{
          minWidth: isMobile ? "800px" : "100%",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <SortableHeader
                columnKey={SORTABLE_COLUMNS.STATUS}
                sx={{
                  width: `${statusColumnWidth}px`,
                  minWidth: `${statusColumnWidth}px`,
                  maxWidth: `${statusColumnWidth}px`,
                  pl: "15px !important",
                  pr: "0px !important",
                }}
                isSticky={true}
                stickyPosition="left"
              >
                {t(COLUMN_NAME_STATUS)}
              </SortableHeader>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{
                  width: `${alertColumnWidth}px`,
                  minWidth: `${alertColumnWidth}px`,
                  maxWidth: `${alertColumnWidth}px`,
                  px: "0px !important",
                  textAlign: "center",
                }}
                isSticky={true}
                stickyPosition="left"
                stickyOffset={statusColumnWidth}
              >
                {/* Columna vacía para el icono de alerta */}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{
                  pl: "16px !important",
                  minWidth: loading ? 274 : 250,
                  width: 250,
                  maxWidth: 250,
                }}
                isSticky={true}
                stickyPosition="left"
                stickyOffset={statusColumnWidth + alertColumnWidth}
              >
                {t(COLUMN_NAME_CAMPAIGN)}
              </TableCell>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ width: "120px" }}
              >
                {t(COLUMN_NAME_PLATFORM)}
              </TableCell>
              {columnVisibility.impressions && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.IMPRESSIONS}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_IMPRESSIONS)}
                </SortableHeader>
              )}
              {columnVisibility.clicks && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.CLICKS}
                  sx={{ width: "100px" }}
                >
                  {t(COLUMN_NAME_CLICKS)}
                </SortableHeader>
              )}
              {columnVisibility.ctr && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.CTR}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_CTR)}
                </SortableHeader>
              )}
              {columnVisibility.spend && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.SPEND}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_SPEND)}
                </SortableHeader>
              )}
              {columnVisibility.roas && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.ROAS}
                  sx={{ width: "120px" }}
                >
                  {t(ROAS_METRICS)}
                </SortableHeader>
              )}
              {columnVisibility.cpm && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.CPM}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_CPM)}
                </SortableHeader>
              )}
              {columnVisibility.cpc && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.CPC}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_CPC)}
                </SortableHeader>
              )}
              {columnVisibility.results && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.RESULTS}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_RESULTS)}
                </SortableHeader>
              )}
              {columnVisibility.cpa && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.CPA}
                  sx={{ width: "120px" }}
                >
                  {t(COLUMN_NAME_CPA)}
                </SortableHeader>
              )}
              {columnVisibility.frequency && (
                <SortableHeader
                  columnKey={SORTABLE_COLUMNS.FREQUENCY}
                  sx={{ width: "120px" }}
                >
                  {t(FREQUENCY)}
                </SortableHeader>
              )}

              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ width: "150px" }}
              >
                {t(COLUMN_NAME_CREATED_BY)}
              </TableCell>
              <SortableHeader
                columnKey={SORTABLE_COLUMNS.CREATION_TIME}
                sx={{ width: "150px" }}
              >
                {t(COLUMN_NAME_CREATED_DATE)}
              </SortableHeader>
              <TableCell
                {...commonPropsTableCellHeader}
                sx={{ width: "120px", pl: "15px !important" }}
              >
                {t(COLUMN_NAME_ACTIONS)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(loading || isSearching) && (
              <TableRow>
                <TableCell colSpan={17} align="center">
                  <Loader size={24} hasMarginTop={false} />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !isSearching &&
              keys.length === 0 &&
              searchTerm &&
              searchTerm.length >= MIN_LENGTH_TO_SEARCH && (
                <TableRow>
                  <TableCell colSpan={17} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t(CAMPAIGNS_NOT_FOUND)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            {!loading &&
              !isSearching &&
              keys.length > 0 &&
              true &&
              keys.map((row, index) => {
                const searchActive =
                  searchTerm && searchTerm.length >= MIN_LENGTH_TO_SEARCH;
                const sourceData = searchActive ? searchResults : data || {};
                const campaign = sourceData[row];
                if (!campaign) {
                  return null;
                }
                const campaignID = campaign.id;
                const status = getStatusAdsCampaign({
                  states: campaign.states,
                  target: "general",
                });
                const user = users[campaign.createdBy];
                const isLoadingStatistics =
                  statisticsLoading.includes(campaignID);
                const isSyncing = syncingCampaignIDs.includes(campaignID);
                const isSwitchingStatus =
                  updatingStatusCampaignIDs.includes(campaignID);
                const isLastRow = index === keys.length - 1;

                return (
                  <TableRow
                    hover
                    onClick={() =>
                      navigate(`/ads-campaign?campaignID=${campaignID}`)
                    }
                    key={index}
                  >
                    <TableCell
                      {...commonPropsTableCellBody}
                      showTooltip={false}
                      isLastRow={isLastRow}
                      isSticky={true}
                      stickyPosition="left"
                      sx={{
                        width: `${statusColumnWidth}px`,
                        minWidth: `${statusColumnWidth}px`,
                        maxWidth: `${statusColumnWidth}px`,
                        pl: "12px !important",
                        pr: "0px !important",
                        overflow: "visible",
                        zIndex: 1001,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.75,
                        }}
                      >
                        <StatusSwitch
                          status={status}
                          active={status === ADS_CAMPAIGN_STATUS_ACTIVE}
                          isSwitching={isSwitchingStatus}
                          readOnly={!canEditCampaign}
                          onChangeStatus={async () => {
                            onChangeStatusCampaign(row);
                          }}
                        />
                        {isSyncing && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              pl: 0.5,
                              minWidth: 0,
                              mt: 0.25,
                            }}
                          >
                            <Loader size={15} hasMarginTop={false} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      {...commonPropsTableCellBody}
                      showTooltip={false}
                      isLastRow={isLastRow}
                      isSticky={true}
                      stickyPosition="left"
                      stickyOffset={statusColumnWidth}
                      sx={{
                        width: `${alertColumnWidth}px`,
                        minWidth: `${alertColumnWidth}px`,
                        maxWidth: `${alertColumnWidth}px`,
                        px: "0px !important",
                        textAlign: "center",
                      }}
                    >
                      <AlertIcon
                        platformsData={campaign.platformsData}
                        platforms={campaign.platforms}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        pl: "16px !important",
                        minWidth: 250,
                        width: 250,
                        maxWidth: 250,
                      }}
                      sxContainerTooltip={{
                        minWidth: 250,
                        width: 250,
                        maxWidth: 250,
                      }}
                      {...commonPropsTableCellBody}
                      isLastRow={isLastRow}
                      isSticky={true}
                      stickyPosition="left"
                      stickyOffset={statusColumnWidth + alertColumnWidth}
                      showTooltip={false}
                    >
                      <Typography
                        variant="bod2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {campaign?.platformsData?.[campaign?.platforms?.[0]]
                          ?.name || campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      {...commonPropsTableCellBody}
                      isLastRow={isLastRow}
                      showTooltip={false}
                    >
                      <PlatformsItem platforms={campaign.platforms} />
                    </TableCell>
                    {columnVisibility.impressions && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="impressions"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="impressions"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.clicks && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="clicks"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="clicks"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.ctr && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="ctr"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="ctr"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.spend && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="spend"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="spend"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.roas && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="roas"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="roas"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.cpm && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="cpm"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="cpm"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}

                    {columnVisibility.cpc && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="cpc"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="cpc"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.results && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="mainAction"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="mainAction"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.cpa && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="cpa"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="cpa"
                          isLoading={statisticsLoading.includes(campaignID)}
                        />
                      </TableCell>
                    )}
                    {columnVisibility.frequency && (
                      <TableCell
                        {...commonPropsTableCellBody}
                        isLastRow={isLastRow}
                        tooltipComponent={
                          <CampaignMetric
                            campaign={campaign}
                            type="frequency"
                            isLoading={isLoadingStatistics}
                            applyFormat={false}
                          />
                        }
                      >
                        <CampaignMetric
                          campaign={campaign}
                          type="frequency"
                          isLoading={isLoadingStatistics}
                        />
                      </TableCell>
                    )}
                    <TableCell
                      {...commonPropsTableCellBody}
                      isLastRow={isLastRow}
                      showTooltip={false}
                    >
                      {user?.name || t(LOADING)}
                    </TableCell>
                    <TableCell
                      {...commonPropsTableCellBody}
                      isLastRow={isLastRow}
                      showTooltip={false}
                    >
                      {campaign.creationTime &&
                        t(DATE_SHORT_2, {
                          date: new Date(campaign.creationTime),
                        })}
                    </TableCell>
                    <TableCell
                      {...commonPropsTableCellBody}
                      showTooltip={false}
                      isLastRow={isLastRow}
                    >
                      <CampaignActions
                        campaign={campaign}
                        canEditCampaign={canEditCampaign}
                        canCreateCampaign={canCreateCampaign}
                        isDeletingCampaign={isDeletingCampaign[row]}
                        onDelete={(e) =>
                          onDeleteDraftCampaign(e, row, campaign, status)
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            {/* Fila de carga - aparece cuando hay más campañas por cargar */}
            {!loadedLastCampaign &&
              !loading &&
              !(searchTerm && searchTerm.length >= MIN_LENGTH_TO_SEARCH) &&
              !isSearching && (
                <TableRow
                  ref={lastItemRef}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent !important",
                    },
                  }}
                >
                  <TableCell
                    colSpan={17}
                    align="center"
                    sx={{
                      height: "80px !important",
                      padding: "20px !important",
                      border: "none",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Loader size={24} hasMarginTop={false} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const PlatformsItem = ({ platforms }) => (
  <Box
    sx={{
      display: "flex",
    }}
  >
    <Box sx={{ display: "flex", gap: 1 }}>
      {platforms?.map((platform) => (
        <Avatar
          key={platform}
          src={logoPlatform(platform)}
          alt={`${platform} Logo`}
          variant="square"
          sx={{
            width: platform === META ? 25 : 20,
            height: "auto",
            ".MuiAvatar-img": { objectFit: "contain" },
          }}
        />
      ))}
    </Box>
  </Box>
);

const CampaignActions = ({
  campaign,
  canEditCampaign,
  canCreateCampaign,
  isDeletingCampaign,
  onDelete,
}) => {
  const { t } = useTranslationApp();

  const hasStatistics = Object.keys(campaign.statistics || {}).length > 0;
  const canShowDuplicateButton = canEditCampaign || canCreateCampaign;

  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="flex-start"
      alignItems="center"
    >
      {canEditCampaign && (
        <>
          {isDeletingCampaign ? (
            <Loader size={20} hasMarginTop={false} />
          ) : (
            <Tooltip title={t(BUTTON_ARCHIVE_CAMPAIGN)} arrow>
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{
                  color: "background.paperContrastText",
                  width: 32,
                  height: 32,
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}

      {canShowDuplicateButton && (
        <ButtonDuplicateCampaign
          campaignID={campaign.id}
          mode="icon"
          sx={{
            width: 20,
            height: 20,
            color: "background.paperContrastText",
          }}
        />
      )}

      {hasStatistics && (
        <Tooltip title={t(REPORTS)} arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e?.stopPropagation();
              openLink(`/ads-campaign-statistics?campaignID=${campaign.id}`);
            }}
            sx={{
              color: "background.paperContrastText",
              width: 32,
              height: 32,
            }}
          >
            <ChartColumnBig />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

const CampaignMetric = ({
  campaign,
  type,
  isLoading = false,
  applyFormat = true,
}) => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const statistics = campaign.statistics || {};
  const status = getStatusAdsCampaign({
    states: campaign.states,
    target: "general",
  });
  const hasMetrics = !_.isEmpty(statistics);
  const isCampaignDraft = status === ADS_CAMPAIGN_STATUS_DRAFT;
  const generalStatistics = getGeneralStatistics(statistics);

  if (isLoading) {
    return <SkeletonCampaignMetric />;
  }

  if (isCampaignDraft) return "-";
  if (status !== ADS_CAMPAIGN_STATUS_ACTIVE && !generalStatistics.impressions)
    return "-";
  if (!hasMetrics || !generalStatistics.impressions) return "-";

  const platforms = Object.keys(statistics || {}).filter(
    (platform) => platform !== "total"
  );
  const currenciesPlatform = {};
  const currencies = [];
  platforms.forEach((platform) => {
    const platformData = campaign?.platformsData?.[platform] || {};
    if (_.isEmpty(platformData)) return;
    const currency = dispatch(
      getCurrencyAdAccountPlatform({
        platform,
        integrationID: platformData?.integrationID,
        adAccountID: platformData?.adAccountID,
      })
    );
    if (!currency) return;
    currenciesPlatform[platform] = currency;
    if (!currencies.includes(currency)) {
      currencies.push(currency);
    }
  });

  const hasUniqueCurrency = currencies.length === 1;

  const formatValue = (value) => {
    if (applyFormat) {
      return numberCompactFormat(value);
    }
    if (Number(value) % 1 !== 0) {
      // Value is a decimal
      return value.toFixed(2);
    }
    return t(VALUE_NUMBER, { value });
  };

  const renderCPAValue = () => {
    const generalCPA = generalStatistics?.costPerMainAction;

    if (hasUniqueCurrency) {
      if (!generalCPA) {
        return "-";
      }

      return <>{`${formatValue(generalCPA)} ${currencies[0]}`}</>;
    }

    const platformsWithCPA = Object.keys(currenciesPlatform).filter(
      (platform) => {
        const platformCPA = statistics?.[platform]?.costPerMainAction;
        return platformCPA != null && Number(platformCPA) !== 0;
      }
    );

    if (!platformsWithCPA.length) {
      return "-";
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {platformsWithCPA.map((platform) => (
          <Box
            key={getUniqueID()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
            }}
          >
            <Avatar
              src={logoPlatform(platform)}
              variant="square"
              alt={platform}
              sx={{
                // Conditionals for META because it is wider
                width: platform === META ? 21 : 20,
                pl: platform === META ? 0 : 0.25,
                height: "auto",
                ".MuiAvatar-img": { objectFit: "contain" },
              }}
            />
            <CellTypography
              sx={{
                whiteSpace: "nowrap",
                position: "relative",
                top: "1px",
              }}
            >
              {`${formatValue(
                statistics?.[platform]?.costPerMainAction
              )} ${currenciesPlatform[platform]}`}
            </CellTypography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderValue = () => {
    const hasMainAction = Number(generalStatistics?.mainAction) > 0;
    switch (type) {
      case "mainAction":
        return (
          <>{hasMainAction ? formatValue(generalStatistics.mainAction) : "-"}</>
        );
      case "clicks":
        return (
          <>
            {generalStatistics.click
              ? formatValue(generalStatistics.click)
              : "-"}
          </>
        );
      case "ctr":
        return (
          <>
            {generalStatistics.ctr
              ? `${formatValue(generalStatistics.ctr)}%`
              : "-"}
          </>
        );
      case "impressions":
        return (
          <Tooltip title={generalStatistics.impressions}>
            <>
              {generalStatistics.impressions
                ? formatValue(generalStatistics.impressions)
                : "-"}
            </>
          </Tooltip>
        );
      case "cpc":
        return (
          <>
            {generalStatistics.cpc ? formatValue(generalStatistics.cpc) : "-"}
          </>
        );
      case "cpm":
        return (
          <>
            {generalStatistics.cpm ? formatValue(generalStatistics.cpm) : "-"}
          </>
        );
      case "cpa":
        return renderCPAValue();
      case "frequency":
        return (
          <>
            {generalStatistics.frequency
              ? formatValue(generalStatistics.frequency)
              : "-"}
          </>
        );
      case "spend":
        return (
          <>
            {hasUniqueCurrency && (
              <>{`${formatValue(generalStatistics.spend)} ${currencies[0]}`}</>
            )}
            {!hasUniqueCurrency && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {Object.keys(currenciesPlatform).map((platform) => (
                  <Box
                    key={getUniqueID()}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Avatar
                      src={logoPlatform(platform)}
                      variant="square"
                      alt={platform}
                      sx={{
                        // Conditionals for META because it is wider
                        width: platform === META ? 21 : 20,
                        pl: platform === META ? 0 : 0.25,
                        height: "auto",
                        ".MuiAvatar-img": { objectFit: "contain" },
                      }}
                    />
                    <CellTypography
                      sx={{
                        whiteSpace: "nowrap",
                        position: "relative",
                        top: "1px",
                      }}
                    >
                      {`${formatValue(statistics?.[platform]?.spend)} ${currenciesPlatform[platform]}`}
                    </CellTypography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        );
      case "roas":
        return Number(generalStatistics.roas) > 0 ? (
          <>{`${numberDecimal(Number(generalStatistics.roas))}x`}</>
        ) : (
          "-"
        );
      default:
        return "-";
    }
  };

  return renderValue();
};

const StatusChip = ({ children, bgcolor, borderColor }) => (
  <Typography
    variant="caption"
    sx={{
      px: 1.25,
      py: 0.25,
      lineHeight: 1.2,
      borderRadius: 1,
      bgcolor,
      border: `1px solid ${borderColor}`,
      color: "#333333",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      fontSize: "0.7rem",
      whiteSpace: "nowrap",
      display: "inline-block",
    }}
  >
    {children}
  </Typography>
);

const StatusSwitch = ({
  status,
  active,
  isSwitching,
  onChangeStatus,
  readOnly = false,
}) => {
  const { t } = useTranslationApp();

  if (status === ADS_CAMPAIGN_STATUS_FINISHED) {
    return (
      <StatusChip
        bgcolor="rgba(158, 158, 158, 0.1)"
        borderColor="rgba(158, 158, 158, 0.3)"
      >
        {t(CAMPAIGNS_STATUS_FINISHED)}
      </StatusChip>
    );
  }

  if (status === ADS_CAMPAIGN_STATUS_DELETED) {
    return (
      <StatusChip
        bgcolor="rgba(158, 158, 158, 0.1)"
        borderColor="rgba(158, 158, 158, 0.3)"
      >
        {t(CAMPAIGNS_STATUS_DELETED)}
      </StatusChip>
    );
  }

  if (status === ADS_CAMPAIGN_STATUS_DRAFT) {
    return (
      <StatusChip
        bgcolor="rgba(255, 193, 7, 0.1)"
        borderColor="rgba(255, 193, 7, 0.3)"
      >
        {t(CAMPAIGNS_STATUS_DRAFT)}
      </StatusChip>
    );
  }

  if (readOnly) {
    const isActive = active || status === ADS_CAMPAIGN_STATUS_ACTIVE;

    return (
      <StatusChip
        bgcolor={isActive ? "rgba(19, 115, 51, 0.1)" : "rgba(197, 34, 31, 0.1)"}
        borderColor={
          isActive ? "rgba(19, 115, 51, 0.3)" : "rgba(197, 34, 31, 0.3)"
        }
      >
        {t(isActive ? STATUS_ACTIVE : STATUS_DEACTIVATED)}
      </StatusChip>
    );
  }

  return (
    <Box sx={{ display: "flex", pl: 0.5 }}>
      <Switch
        hasLabel={false}
        isActive={active}
        size="small"
        loading={isSwitching}
        disabled={isSwitching || readOnly}
        onChangeIsActive={readOnly ? undefined : onChangeStatus}
        sx={{ mt: 0.25 }}
      />
    </Box>
  );
};

const AlertIcon = ({ platformsData, platforms }) => {
  const { t } = useTranslationApp();

  const allAlerts = [];

  if (platforms && platformsData) {
    platforms.forEach((platform) => {
      const platformData = platformsData[platform];

      if (platformData?.alerts && platformData.alerts.length > 0) {
        const alertsByType = new Map();

        platformData.alerts.forEach((alert) => {
          if (alert.type && !alertsByType.has(alert.type)) {
            alertsByType.set(alert.type, {
              platform: platform,
              alertType: alert.type,
              longMessage: alert.longMessage,
              shortMessage: alert.shortMessage,
            });
          }
        });

        alertsByType.forEach((alertData) => {
          allAlerts.push(alertData);
        });
      }
    });
  }

  const getAlertMessage = (alert) => {
    switch (alert.alertType) {
      case "ALERT_DISAPPROVALS_ISSUES":
        return t(YOUR_CAMPAIGN_HAS_SOME_DISAPPROVALS);
      default: {
        const alertTranslation = ALERTS_CAMPAIGNS_TEXT_TABLE[alert.alertType]
          ? t(ALERTS_CAMPAIGNS_TEXT_TABLE[alert.alertType], {
              platform: t(alert.platform),
            })
          : null;

        return (
          alertTranslation?.trim() ||
          alert.shortMessage ||
          alert.longMessage ||
          ""
        );
      }
    }
  };

  const alertsWithMessages = allAlerts.filter((alert) => {
    const message = getAlertMessage(alert);
    return message;
  });

  const isErrorAlert = alertsWithMessages.some(
    (alert) => !YELLOW_ALERT_TYPES.includes(alert.alertType)
  );

  const iconColor = isErrorAlert ? ALERT_COLORS.error : ALERT_COLORS.yellow;

  if (alertsWithMessages.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Tooltip
        title={
          <Box>
            {alertsWithMessages.map((item, index) => (
              <Typography
                key={`${item.platform}-${item.alertType}-${index}`}
                sx={{ fontSize: "0.875rem", color: "white" }}
                variant="body2"
              >
                {item.platform.charAt(0).toUpperCase() + item.platform.slice(1)}
                : {getAlertMessage(item)}
              </Typography>
            ))}
          </Box>
        }
        arrow
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            backgroundColor: iconColor.background,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            component="span"
            sx={{
              color: "#ffffff",
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            !
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
};

export { CampaignMetric, StatusSwitch, PlatformsItem };

export default AdsCampaignsTable;
